import { Injectable, Injector } from '@angular/core';
import { AccountProductionService } from '@app/core/Services';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import {
  DeleteTBJurnal,
  GetAutoRoundingCompanySetting,
  GetAccountProductionTrialBalance,
  GetAccountProductionTrialBalanceByJurnId,
  GetAllJournalsBasedOnAccountingPeriodId,
  GetAutoRoundingSettingAccounts,
  GetAutoRoundingSettingById,
  GetAutoRoundingSettingList,
  RefreshAccountProductionTrialBalance,
  SaveAccountProductionTrialBalance,
  SaveAutoRoundingSetting,
  UpdateAutoRoundingCompanySetting,
  UpdateAutoRoundingSetting,
  DeleteAutoRoundingSetting,
} from './account-production.action';
import { Guid } from 'guid-typescript';

export class AccountProductionStateInfo {
  isLoading?: boolean;
  refreshTrialBalance?: any;
  openingBalanceData: any;
  isTrialBalance: boolean;
  jurnalsList: any;
  isSuccess?:boolean;
  tbId?:Guid;
  autoRoundingSetting?: any;
  autoRoundingSettingAccounts?: any;
  autoRoundingSettingList?: any[];
  autoRoundingSettingDetail?: any;
  autoRoundingCompanySetting?: any;
}

@State<AccountProductionStateInfo>({
  name: 'accountProduction',
  defaults: {
    openingBalanceData: [],
    isTrialBalance: false,
    jurnalsList: [],
    isSuccess:false,
    autoRoundingSetting: null
  },
})
@Injectable()
export class AccountProductionState {
  accountProductionService: AccountProductionService;
  constructor(private injector: Injector) {
    this.accountProductionService = injector.get<AccountProductionService>(
      AccountProductionService
    );
  }
  @Selector()
  static getJournalAccounts(state: AccountProductionStateInfo) {
    return state.openingBalanceData;
  }
  @Selector()
  static isLoading(state: AccountProductionStateInfo): boolean {
    return state.isLoading ?? false;
  }
  @Selector()
  static jurnalsList(state: AccountProductionStateInfo): any {
    return state.jurnalsList; 
  }

  @Selector()
  static getAutoRoundingSettingAccounts(state: AccountProductionStateInfo): any {
    return state.autoRoundingSettingAccounts;
  }

  @Selector()
  static getAutoRoundingSettingList(state: AccountProductionStateInfo): any[] {
    return state.autoRoundingSettingList ?? [];
  }

  @Selector()
  static getAutoRoundingSettingDetail(state: AccountProductionStateInfo): any {
    return state.autoRoundingSettingDetail;
  }

  @Selector()
  static getAutoRoundingCompanySetting(state: AccountProductionStateInfo): any {
    return state.autoRoundingCompanySetting;
  }

  @Action(RefreshAccountProductionTrialBalance, { cancelUncompleted: true })
  getMainList(
    { patchState }: StateContext<AccountProductionStateInfo>,
    action: RefreshAccountProductionTrialBalance
  ) {
    return this.accountProductionService
      .refreshAccountProductionTrialBalance(action.accountingPeriodId)
      .pipe(
        tap({
          next: (result) => {
            patchState({
              refreshTrialBalance: result,
            });
          },
          error: () => {
            patchState({ isLoading: false });
          },
        })
      );
  }

  @Action(GetAccountProductionTrialBalance)
  getAccountProductionTrialBalance(
    { getState, patchState }: StateContext<AccountProductionStateInfo>,
    action: GetAccountProductionTrialBalance
  ) {
    return this.accountProductionService
      .getAccountProductionTrialBalance(action.accoutingPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            openingBalanceData: res,
          });
        })
      );
  }

  @Action(SaveAccountProductionTrialBalance)
  saveAccountProductionTrialBalance(
    { patchState }: StateContext<AccountProductionStateInfo>,
    action: SaveAccountProductionTrialBalance
  ) {
    return this.accountProductionService
      .saveTrialBalance(action.trialBalance)
      .pipe(
        tap((res) => {
          patchState({
            isTrialBalance: res.status,
            tbId:res.journalId,
          });
        })
      );
  }

  @Action(GetAccountProductionTrialBalanceByJurnId)
  getAccountProductionTrialBalanceById(
    { getState, patchState }: StateContext<AccountProductionStateInfo>,
    action: GetAccountProductionTrialBalanceByJurnId
  ) {
    return this.accountProductionService
      .getAccountProductionTrialBalanceByJurnal(action.jurnalId)
      .pipe(
        tap((res) => {
          patchState({
            openingBalanceData: res,
          });
        })
      );
  }

  @Action(GetAllJournalsBasedOnAccountingPeriodId)
  getAllJournalsBasedOnAccountingPeriodId(
    { getState, patchState }: StateContext<AccountProductionStateInfo>,
    action: GetAllJournalsBasedOnAccountingPeriodId
  ) {
    return this.accountProductionService
      .getAllJournalsBasedOnAccountingPeriodId(action.periodId)
      .pipe(
        tap((res) => {
          patchState({
            jurnalsList: res,
          });
        })
      );
  }

  @Action(DeleteTBJurnal)
  deleteTBJurnalByJurnalId(
    { getState, patchState }: StateContext<AccountProductionStateInfo>,
    action: DeleteTBJurnal
  ) {
    return this.accountProductionService
      .deleteTBJurnal(action.jurnalId)
      .pipe(
        tap((res:any) => {
          patchState({
            isSuccess: res,
          });
        })
      );
  }

  @Action(GetAutoRoundingSettingAccounts)
  getAutoRoundingSettingAccounts(
    { patchState }: StateContext<AccountProductionStateInfo>
  ) {
    return this.accountProductionService
      .getAutoRoundingSettingAccounts()
      .pipe(
        tap((res: any) => {
          patchState({
            autoRoundingSettingAccounts: res,
          });
        })
      );
  }

  @Action(SaveAutoRoundingSetting)
  saveAutoRoundingSetting(
    { patchState }: StateContext<AccountProductionStateInfo>,
    action: SaveAutoRoundingSetting
  ) {
    return this.accountProductionService
      .saveAutoRoundingSetting(action.payload)
      .pipe(
        tap((res: any) => {
          patchState({ autoRoundingSetting: res });
        })
      );
  }

  @Action(GetAutoRoundingSettingList)
  getAutoRoundingSettingList(
    { patchState }: StateContext<AccountProductionStateInfo>
  ) {
    return this.accountProductionService
      .getAutoRoundingSettingList()
      .pipe(
        tap((res: any[]) => {
          patchState({ autoRoundingSettingList: res });
        })
      );
  }

  @Action(GetAutoRoundingSettingById)
  getAutoRoundingSettingById(
    { patchState }: StateContext<AccountProductionStateInfo>,
    action: GetAutoRoundingSettingById
  ) {
    return this.accountProductionService
      .getAutoRoundingSettingById(action.id)
      .pipe(
        tap((res: any) => {
          patchState({ autoRoundingSettingDetail: res });
        })
      );
  }

  @Action(UpdateAutoRoundingSetting)
  updateAutoRoundingSetting(
    { patchState }: StateContext<AccountProductionStateInfo>,
    action: UpdateAutoRoundingSetting
  ) {
    return this.accountProductionService
      .updateAutoRoundingSetting(action.id, action.payload)
      .pipe(
        tap((res: any) => {
          patchState({ autoRoundingSetting: res });
        })
      );
  }

  @Action(DeleteAutoRoundingSetting)
  deleteAutoRoundingSetting(
    { patchState }: StateContext<AccountProductionStateInfo>,
    action: DeleteAutoRoundingSetting
  ) {
    return this.accountProductionService
      .deleteAutoRoundingSetting(action.id)
      .pipe(
        tap((res: any) => {
          patchState({ isSuccess: res });
        })
      );
  }

  @Action(GetAutoRoundingCompanySetting)
  getAutoRoundingCompanySetting(
    { patchState }: StateContext<AccountProductionStateInfo>
  ) {
    return this.accountProductionService
      .getAutoRoundingCompanySetting()
      .pipe(
        tap((res: any) => {
          patchState({ autoRoundingCompanySetting: res });
        })
      );
  }

  @Action(UpdateAutoRoundingCompanySetting)
  updateAutoRoundingCompanySetting(
    { getState, patchState }: StateContext<AccountProductionStateInfo>,
    action: UpdateAutoRoundingCompanySetting
  ) {
    return this.accountProductionService
      .updateAutoRoundingCompanySetting(action.isAutoRounding)
      .pipe(
        tap((res: any) => {
          const currentCompanySetting = getState().autoRoundingCompanySetting;
          const updatedIsAutoRounding =
            typeof res === 'boolean' ? res : action.isAutoRounding;

          patchState({
            isSuccess: res,
            autoRoundingCompanySetting: {
              companyId: currentCompanySetting?.companyId,
              isAutoRounding: updatedIsAutoRounding,
            },
          });
        })
      );
  }
}